/**
 * CREDENTIALS BACKUP & RECOVERY MANAGER
 *
 * Solusi "Scan Sekali, Selamanya" untuk WhatsApp Session
 *
 * Fitur:
 * 1. Auto-backup credentials setiap kali update
 * 2. Validation setelah save untuk memastikan file tidak rusak
 * 3. Auto-restore dari backup jika file rusak
 * 4. Health check saat startup
 * 5. Multiple backup rotation (simpan beberapa backup terakhir)
 */

import fs from "fs/promises";
import fsSync from "fs";
import path from "path";
import { fileURLToPath } from "url";
import { dirname } from "path";

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// ============================================
// KONFIGURASI
// ============================================
const CREDENTIALS_CONFIG = {
    // Jumlah backup yang disimpan (rotasi)
    maxBackups: 5,

    // Nama file credentials
    credsFileName: "creds.json",

    // Nama file backup
    backupPrefix: "creds.backup",

    // Interval health check (ms)
    healthCheckInterval: 5 * 60 * 1000, // 5 menit

    // Enable debug logging
    debug: true,

    // ============================================
    // APP STATE SYNC CLEANUP CONFIG
    // Mencegah session folder tumbuh terlalu besar
    // ============================================

    // Max ukuran session folder sebelum warning (bytes)
    maxSessionFolderSize: 10 * 1024 * 1024, // 10MB (warning threshold)

    // Max ukuran session folder sebelum cleanup agresif (bytes)
    aggressiveCleanupThreshold: 50 * 1024 * 1024, // 50MB (cleanup agresif)

    // ============================================
    // FILE CLEANUP POLICY
    // ⚠️ HATI-HATI: Jangan hapus file yang penting!
    // ============================================

    // Files yang TIDAK BOLEH dihapus (critical files)
    protectedFiles: [
        "creds.json",
        "creds.backup",
    ],

    // File patterns yang BOLEH di-cleanup (dengan syarat)
    // - session-*.json: cache chat (boleh hapus yang lama)
    // - app-state-sync-*.json: app state cache (boleh hapus yang besar/old)
    cleanablePatterns: [
        "session-",           // Chat session cache (boleh hapus yang lama)
        "app-state-sync-",    // App state cache (boleh hapus yang besar)
    ],

    // File patterns yang TIDAK BOLEH dihapus (critical)
    protectedPatterns: [
        "sender-key-",        // Encryption keys (CRITICAL!)
        "lid-mapping-",       // Message ID mapping (important)
        "app-state-sync-key-", // Sync keys (important)
    ],

    // Age limit untuk cleanable files (hapus jika lebih lama dari ini)
    maxCleanableFileAge: 30 * 24 * 60 * 60 * 1000, // 30 hari

    // Min age untuk session files (jangan hapus session yang baru)
    minSessionFileAge: 7 * 24 * 60 * 60 * 1000, // 7 hari
};

// ============================================
// LOGGER
// ============================================
const debugLog = (message, ...args) => {
    if (CREDENTIALS_CONFIG.debug) {
        console.log(`[CredentialsManager] ${message}`, ...args);
    }
};

const errorLog = (message, ...args) => {
    console.error(`[CredentialsManager] ${message}`, ...args);
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get sessions base directory path
 * Secara otomatis mendeteksi lokasi folder sessions
 * Mendukung development dan production (dist/) environment
 *
 * Struktur yang didukung:
 * - Development: utils/ -> sessions/
 * - Production: server.js di root/, sessions di dist/sessions/
 */
const getSessionsBaseDir = () => {
    // ============================================
    // PRIORITY 1: Environment Variable (Production)
    // ============================================
    // Di production, set SESSIONS_DIR ke persistent storage seperti:
    // /var/lib/whatsapp-sessions atau /home/whatsapp/sessions
    if (process.env.SESSIONS_DIR) {
        const envPath = process.env.SESSIONS_DIR;
        if (fsSync.existsSync(envPath)) {
            debugLog(`📂 Using SESSIONS_DIR from environment: ${envPath}`);
            return envPath;
        }
        debugLog(`⚠️ SESSIONS_DIR set but not found: ${envPath}, creating...`);
        fsSync.mkdirSync(envPath, { recursive: true });
        return envPath;
    }

    // ============================================
    // PRIORITY 2: Default paths (Development/Fallback)
    // ============================================
    // Gunakan process.cwd() untuk mendapatkan working directory
    // Ini lebih reliable daripada __dirname di bundled code
    const cwd = process.cwd();

    // Possible paths (diurutkan berdasarkan prioritas)
    const possiblePaths = [
        // Production: dari working directory ke dist/sessions
        path.join(cwd, "dist", "sessions"),
        // Alternative: dari __dirname (jika defined)
        ...(typeof __dirname !== "undefined" ? [
            path.join(__dirname, "dist", "sessions"),
            path.join(__dirname, "..", "dist", "sessions"),
            path.join(__dirname, "sessions"),
            path.join(__dirname, "..", "sessions"),
        ] : []),
        // Last resort: dari current directory
        path.join("dist", "sessions"),
        path.join("sessions"),
    ];

    // Cek setiap path dan return yang pertama valid
    for (const sessionsPath of possiblePaths) {
        try {
            // Cek apakah directory ada
            if (fsSync.existsSync(sessionsPath)) {
                const stat = fsSync.statSync(sessionsPath);
                if (stat.isDirectory()) {
                    debugLog(`📂 Using sessions path: ${sessionsPath}`);
                    return sessionsPath;
                }
            }
        } catch (error) {
            // Path tidak valid, lanjut ke berikutnya
            continue;
        }
    }

    // Fallback default: gunakan cwd + dist/sessions (production default)
    const fallbackPath = path.join(cwd, "dist", "sessions");
    debugLog(`⚠️ Using fallback sessions path: ${fallbackPath}`);
    return fallbackPath;
};

/**
 * Get session directory path untuk session tertentu
 */
const getSessionDir = (sessionId) => {
    return path.join(getSessionsBaseDir(), sessionId);
};

/**
 * Get credentials file path
 */
const getCredsPath = (sessionId) => {
    return path.join(getSessionDir(sessionId), CREDENTIALS_CONFIG.credsFileName);
};

/**
 * Get backup file path dengan timestamp
 */
const getBackupPath = (sessionId, timestamp = Date.now()) => {
    const timestampStr = new Date(timestamp).toISOString().replace(/[:.]/g, "-");
    return path.join(
        getSessionDir(sessionId),
        `${CREDENTIALS_CONFIG.backupPrefix}.${timestampStr}.json`
    );
};

/**
 * Get semua backup file untuk session tertentu
 */
const getBackupFiles = async (sessionId) => {
    try {
        const sessionDir = getSessionDir(sessionId);
        const files = await fs.readdir(sessionDir);
        return files
            .filter((f) => f.startsWith(CREDENTIALS_CONFIG.backupPrefix) && f.endsWith(".json"))
            .sort()
            .reverse(); // Terbaru dulu
    } catch (error) {
        return [];
    }
};

/**
 * Parse dan validate JSON file
 * Returns { valid: boolean, data: object|null, error: string|null }
 */
const validateJsonFile = async (filePath) => {
    try {
        const content = await fs.readFile(filePath, "utf-8");

        // Cek jika file kosong
        if (!content || content.trim().length === 0) {
            return {
                valid: false,
                data: null,
                error: "File is empty",
            };
        }

        // Cek jika file terpotong (JSON tidak lengkap)
        if (!content.trim().endsWith("}")) {
            return {
                valid: false,
                data: null,
                error: "Unexpected end of JSON input (truncated file)",
            };
        }

        // Try parse JSON
        const data = JSON.parse(content);

        // Validate structure minimal
        if (!data || typeof data !== "object") {
            return {
                valid: false,
                data: null,
                error: "Invalid data structure (not an object)",
            };
        }

        return {
            valid: true,
            data,
            error: null,
        };
    } catch (error) {
        return {
            valid: false,
            data: null,
            error: error.message,
        };
    }
};

/**
 * Hapus backup lama (rotasi)
 */
const cleanupOldBackups = async (sessionId) => {
    try {
        const backups = await getBackupFiles(sessionId);

        if (backups.length > CREDENTIALS_CONFIG.maxBackups) {
            // Hapus backup terlama
            const toDelete = backups.slice(CREDENTIALS_CONFIG.maxBackups);

            for (const file of toDelete) {
                const filePath = path.join(getSessionDir(sessionId), file);
                await fs.unlink(filePath);
                debugLog(`🗑️ Deleted old backup: ${file}`);
            }
        }
    } catch (error) {
        errorLog(`Error cleaning up backups for ${sessionId}:`, error);
    }
};

// ============================================
// CORE FUNCTIONS
// ============================================

/**
 * Buat backup credentials
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} - True jika berhasil
 */
const backupCredentials = async (sessionId) => {
    const credsPath = getCredsPath(sessionId);

    try {
        // Baca file credentials saat ini
        const validation = await validateJsonFile(credsPath);

        if (!validation.valid) {
            errorLog(`⚠️ Cannot backup ${sessionId} - credentials invalid: ${validation.error}`);
            return false;
        }

        // Buat backup baru dengan timestamp
        const backupPath = getBackupPath(sessionId);
        await fs.writeFile(backupPath, JSON.stringify(validation.data, null, 2));

        debugLog(`✅ Backup created for ${sessionId}: ${path.basename(backupPath)}`);

        // Cleanup backup lama
        await cleanupOldBackups(sessionId);

        return true;
    } catch (error) {
        errorLog(`❌ Error backing up credentials for ${sessionId}:`, error);
        return false;
    }
};

/**
 * Restore credentials dari backup terbaru
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} - True jika berhasil
 */
const restoreFromBackup = async (sessionId) => {
    try {
        const backups = await getBackupFiles(sessionId);

        if (backups.length === 0) {
            debugLog(`⚠️ No backups found for ${sessionId}`);
            return false;
        }

        // Gunakan backup terbaru
        const latestBackup = backups[0];
        const backupPath = path.join(getSessionDir(sessionId), latestBackup);

        // Validate backup
        const validation = await validateJsonFile(backupPath);

        if (!validation.valid) {
            errorLog(`⚠️ Latest backup is corrupted for ${sessionId}: ${validation.error}`);

            // Coba backup kedua jika ada
            if (backups.length > 1) {
                debugLog(`🔄 Trying second latest backup...`);
                const secondBackup = backups[1];
                const secondBackupPath = path.join(getSessionDir(sessionId), secondBackup);

                const secondValidation = await validateJsonFile(secondBackupPath);
                if (secondValidation.valid) {
                    await fs.writeFile(getCredsPath(sessionId), JSON.stringify(secondValidation.data, null, 2));
                    debugLog(`✅ Restored from second backup: ${secondBackup}`);
                    return true;
                }
            }

            return false;
        }

        // Restore backup terbaru
        await fs.writeFile(getCredsPath(sessionId), JSON.stringify(validation.data, null, 2));

        debugLog(`✅ Restored credentials for ${sessionId} from: ${latestBackup}`);
        return true;
    } catch (error) {
        errorLog(`❌ Error restoring credentials for ${sessionId}:`, error);
        return false;
    }
};

/**
 * Safe save credentials dengan validasi dan backup otomatis
 * @param {string} sessionId - Session ID
 * @param {Function} saveCreds - Original saveCreds function dari Baileys
 * @returns {Promise<boolean>} - True jika berhasil
 */
const safeSaveCreds = async (sessionId, saveCreds) => {
    const credsPath = getCredsPath(sessionId);

    try {
        // 1. Panggil original saveCreds
        debugLog(`💾 Saving credentials for ${sessionId}...`);
        await saveCreds();

        // 2. Validate file yang baru disimpan
        const validation = await validateJsonFile(credsPath);

        if (!validation.valid) {
            errorLog(`❌ CREDENTIALS CORRUPTION DETECTED for ${sessionId}: ${validation.error}`);
            errorLog(`   This should NEVER happen - investigate file system issues!`);

            // Coba restore dari backup
            const restored = await restoreFromBackup(sessionId);
            if (restored) {
                debugLog(`✅ Recovered ${sessionId} from backup after corruption`);
                return true;
            }

            errorLog(`❌ No valid backup found for ${sessionId} - session may be lost!`);
            return false;
        }

        // 3. Buat backup setelah save berhasil
        await backupCredentials(sessionId);

        debugLog(`✅ Credentials saved and validated for ${sessionId}`);

        return true;
    } catch (error) {
        errorLog(`❌ Error saving credentials for ${sessionId}:`, error);

        // Coba restore dari backup jika error
        const restored = await restoreFromBackup(sessionId);
        if (restored) {
            debugLog(`✅ Recovered ${sessionId} from backup after save error`);
            return true;
        }

        return false;
    }
};

/**
 * Validate dan auto-recover credentials saat startup
 * @param {string} sessionId - Session ID
 * @returns {Promise<{valid: boolean, recovered: boolean, error: string|null}>}
 */
const validateAndRecoverCredentials = async (sessionId) => {
    const credsPath = getCredsPath(sessionId);

    debugLog(`🔍 Validating credentials for ${sessionId}...`);

    // 1. Cek apakah file credentials ada
    try {
        await fs.access(credsPath);
    } catch (error) {
        debugLog(`⚠️ No credentials file found for ${sessionId}`);
        return {
            valid: false,
            recovered: false,
            error: "No credentials file",
        };
    }

    // 2. Validate credentials
    const validation = await validateJsonFile(credsPath);

    if (validation.valid) {
        debugLog(`✅ Credentials valid for ${sessionId}`);
        return {
            valid: true,
            recovered: false,
            error: null,
        };
    }

    // 3. Credentials rusak - coba restore dari backup
    errorLog(`⚠️ CORRUPTED credentials detected for ${sessionId}: ${validation.error}`);
    errorLog(`   Attempting recovery from backup...`);

    const restored = await restoreFromBackup(sessionId);

    if (restored) {
        // Validate lagi setelah restore
        const revalidation = await validateJsonFile(credsPath);

        if (revalidation.valid) {
            debugLog(`✅ Successfully recovered credentials for ${sessionId}`);
            return {
                valid: true,
                recovered: true,
                error: null,
            };
        }
    }

    errorLog(`❌ Failed to recover credentials for ${sessionId} - requires re-scan!`);
    return {
        valid: false,
        recovered: false,
        error: validation.error,
    };
};

/**
 * Get phone number dari credentials
 * Berguna untuk recovery sessionData setelah server restart
 * @param {string} sessionId - Session ID
 * @returns {Promise<string|null>} - Phone number atau null jika tidak ditemukan
 */
const extractPhoneNumberFromCreds = async (sessionId) => {
    const credsPath = getCredsPath(sessionId);

    try {
        const validation = await validateJsonFile(credsPath);

        if (!validation.valid) {
            return null;
        }

        // Phone number biasanya ada di creds.me atau creds.registration
        const me = validation.data?.me;
        const registration = validation.data?.registration;

        if (me?.user) {
            return me.user;
        }

        if (registration?.phoneNumber) {
            return registration.phoneNumber;
        }

        // Cari di dalam objects
        if (validation.data?.creds) {
            const credsData = validation.data.creds;
            if (credsData.me?.user) {
                return credsData.me.user;
            }
            if (credsData.registration?.phoneNumber) {
                return credsData.registration.phoneNumber;
            }
        }

        return null;
    } catch (error) {
        errorLog(`Error extracting phone number for ${sessionId}:`, error);
        return null;
    }
};

/**
 * Health check semua credentials di system
 * Berguna untuk dijalankan saat server startup
 * @param {string} sessionsDir - Directory sessions
 * @returns {Promise<{total: number, valid: number, corrupted: number, recovered: number}>}
 */
const healthCheckAllCredentials = async (sessionsDir = null) => {
    const dir = sessionsDir || getSessionsBaseDir();

    debugLog(`🏥 Running health check for all credentials in: ${dir}`);

    try {
        const sessionDirs = await fs.readdir(dir);

        const results = {
            total: 0,
            valid: 0,
            corrupted: 0,
            recovered: 0,
            sessions: [],
        };

        for (const sessionDir of sessionDirs) {
            const sessionPath = path.join(dir, sessionDir);
            const stat = await fs.stat(sessionPath);

            // Skip jika bukan directory
            if (!stat.isDirectory()) {
                continue;
            }

            // Cek apakah ada creds.json
            const credsPath = path.join(sessionPath, CREDENTIALS_CONFIG.credsFileName);
            try {
                await fs.access(credsPath);
            } catch (error) {
                // Tidak ada creds.json, skip
                continue;
            }

            results.total++;

            const checkResult = await validateAndRecoverCredentials(sessionDir);

            results.sessions.push({
                sessionId: sessionDir,
                ...checkResult,
            });

            if (checkResult.valid) {
                results.valid++;
            }

            if (checkResult.recovered) {
                results.recovered++;
            }

            if (!checkResult.valid && !checkResult.recovered) {
                results.corrupted++;
            }
        }

        debugLog(`📊 Health check results:`);
        debugLog(`   Total sessions: ${results.total}`);
        debugLog(`   Valid: ${results.valid}`);
        debugLog(`   Recovered: ${results.recovered}`);
        debugLog(`   Corrupted (unrecoverable): ${results.corrupted}`);

        return results;
    } catch (error) {
        errorLog(`Error during health check:`, error);
        return {
            total: 0,
            valid: 0,
            corrupted: 0,
            recovered: 0,
            sessions: [],
        };
    }
};

/**
 * Cleanup all sessions (hapus corrupted sessions tanpa backup)
 * @param {string} sessionsDir - Directory sessions
 * @returns {Promise<{cleaned: number, errors: string[]}>}
 */
const cleanupCorruptedSessions = async (sessionsDir = null) => {
    const dir = sessionsDir || getSessionsBaseDir();

    debugLog(`🧹 Cleaning up corrupted sessions in: ${dir}`);

    try {
        const sessionDirs = await fs.readdir(dir);
        const results = {
            cleaned: 0,
            errors: [],
            cleanedSessions: [],
        };

        for (const sessionDir of sessionDirs) {
            const sessionPath = path.join(dir, sessionDir);
            const stat = await fs.stat(sessionPath);

            if (!stat.isDirectory()) {
                continue;
            }

            const checkResult = await validateAndRecoverCredentials(sessionDir);

            // Jika tidak valid dan tidak bisa di-recover, hapus session
            if (!checkResult.valid && !checkResult.recovered) {
                try {
                    await fs.rm(sessionPath, { recursive: true, force: true });
                    results.cleaned++;
                    results.cleanedSessions.push(sessionDir);
                    debugLog(`🗑️ Cleaned up corrupted session: ${sessionDir}`);
                } catch (cleanupError) {
                    results.errors.push(
                        `Failed to clean ${sessionDir}: ${cleanupError.message}`
                    );
                }
            }
        }

        debugLog(`✅ Cleanup complete: ${results.cleaned} sessions removed`);
        return results;
    } catch (error) {
        errorLog(`Error during cleanup:`, error);
        return { cleaned: 0, errors: [error.message], cleanedSessions: [] };
    }
};

// ============================================
// APP STATE SYNC CLEANUP
// Mencegah session folder tumbuh terlalu besar
// ============================================

/**
 * Get ukuran folder secara rekursif
 */
const getFolderSize = async (folderPath) => {
    let totalSize = 0;

    try {
        const files = await fs.readdir(folderPath);

        for (const file of files) {
            const filePath = path.join(folderPath, file);
            const stats = await fs.stat(filePath);

            if (stats.isDirectory()) {
                totalSize += await getFolderSize(filePath);
            } else {
                totalSize += stats.size;
            }
        }
    } catch (error) {
        // Folder mungkin tidak ada
    }

    return totalSize;
};

/**
 * Format bytes ke human readable
 */
const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Cleanup session files secara AMAN
 * ⚠️ Hanya menghapus file yang aman untuk dihapus
 *
 * @param {string} sessionId - Session ID
 * @returns {Promise<{cleaned: number, freedSpace: number, files: object[], warnings: string[]}>}
 */
const cleanupAppStateFiles = async (sessionId) => {
    const sessionDir = getSessionDir(sessionId);

    try {
        const files = await fs.readdir(sessionDir);

        let cleanedCount = 0;
        let freedSpace = 0;
        const cleanedFiles = [];
        const warnings = [];

        debugLog(`🔍 Analyzing ${files.length} files in ${sessionId}...`);

        for (const file of files) {
            const filePath = path.join(sessionDir, file);
            const stats = await fs.stat(filePath);

            // ============================================
            // LAYER 1: Skip protected files
            // ============================================
            if (CREDENTIALS_CONFIG.protectedFiles.includes(file)) {
                continue;
            }

            // Skip backup files
            if (file.startsWith(CREDENTIALS_CONFIG.backupPrefix)) {
                continue;
            }

            // ============================================
            // LAYER 2: Skip CRITICAL patterns (JANGAN DELETE!)
            // ============================================
            const isProtected = CREDENTIALS_CONFIG.protectedPatterns.some(
                pattern => file.startsWith(pattern)
            );

            if (isProtected) {
                // Critical file - jangan hapus!
                if (stats.size > 1024 * 1024) { // > 1MB
                    warnings.push(`Large critical file: ${file} (${formatBytes(stats.size)})`);
                }
                continue;
            }

            // ============================================
            // LAYER 3: Cleanable patterns (boleh hapus dengan syarat)
            // ============================================
            const isCleanable = CREDENTIALS_CONFIG.cleanablePatterns.some(
                pattern => file.startsWith(pattern)
            );

            if (!isCleanable) {
                // Unknown file - skip untuk safety
                debugLog(`⚠️ Unknown file pattern: ${file} - skipping`);
                continue;
            }

            // ============================================
            // LAYER 4: Age-based cleanup untuk session files
            // ============================================
            const fileAge = Date.now() - stats.mtimeMs;
            const ageDays = Math.floor(fileAge / (24 * 60 * 60 * 1000));

            // session-*.json: hanya hapus yang > 7 hari dan size > 10KB
            if (file.startsWith("session-")) {
                if (fileAge > CREDENTIALS_CONFIG.minSessionFileAge && stats.size > 10 * 1024) {
                    debugLog(`🗑️ Deleting old session file: ${file} (${ageDays} days old, ${formatBytes(stats.size)})`);

                    freedSpace += stats.size;
                    cleanedFiles.push({
                        file,
                        size: stats.size,
                        reason: `Old session cache (${ageDays} days)`,
                    });

                    await fs.unlink(filePath);
                    cleanedCount++;
                }
                continue;
            }

            // ============================================
            // LAYER 5: Size & age-based cleanup untuk app-state-sync files
            // ============================================
            if (file.startsWith("app-state-sync-")) {
                // Hapus jika > 30 hari ATAU size > 1MB
                if (fileAge > CREDENTIALS_CONFIG.maxCleanableFileAge || stats.size > 1024 * 1024) {
                    debugLog(`🗑️ Deleting app state file: ${file} (${ageDays} days old, ${formatBytes(stats.size)})`);

                    freedSpace += stats.size;
                    cleanedFiles.push({
                        file,
                        size: stats.size,
                        reason: stats.size > 1024 * 1024 ? "Too large" : `Old (${ageDays} days)`,
                    });

                    await fs.unlink(filePath);
                    cleanedCount++;
                }
                continue;
            }
        }

        if (cleanedCount > 0) {
            debugLog(`✅ Cleaned up ${cleanedCount} files for ${sessionId}, freed ${formatBytes(freedSpace)}`);
        }

        if (warnings.length > 0) {
            debugLog(`⚠️ Warnings for ${sessionId}:`);
            warnings.forEach(w => debugLog(`   ${w}`));
        }

        return {
            cleaned: cleanedCount,
            freedSpace,
            files: cleanedFiles,
            warnings,
        };
    } catch (error) {
        errorLog(`Error cleaning up files for ${sessionId}:`, error);
        return { cleaned: 0, freedSpace: 0, files: [], warnings: [] };
    }
};

/**
 * Analyze session folder size dan cleanup jika perlu
 * @param {string} sessionId - Session ID
 * @returns {Promise<{before: string, after: string, freed: string, cleaned: number, fileCount: object, warnings: string[]}>}
 */
const analyzeAndCleanupSession = async (sessionId) => {
    const sessionDir = getSessionDir(sessionId);

    try {
        // Cek ukuran folder sebelum cleanup
        const sizeBefore = await getFolderSize(sessionDir);

        // Count file types
        const files = await fs.readdir(sessionDir);
        const fileCount = {
            total: files.length,
            session: 0,
            senderKey: 0,
            lidMapping: 0,
            appState: 0,
            other: 0,
        };

        files.forEach(f => {
            if (f.startsWith("session-")) fileCount.session++;
            else if (f.startsWith("sender-key-")) fileCount.senderKey++;
            else if (f.startsWith("lid-mapping-")) fileCount.lidMapping++;
            else if (f.startsWith("app-state-sync-")) fileCount.appState++;
            else fileCount.other++;
        });

        // Berikan warning jika size besar
        if (sizeBefore > CREDENTIALS_CONFIG.maxSessionFolderSize) {
            debugLog(`⚠️ Session ${sessionId} folder is large: ${formatBytes(sizeBefore)} (${fileCount.total} files)`);
            debugLog(`   Session files: ${fileCount.session}`);
            debugLog(`   Sender keys: ${fileCount.senderKey} (protected)`);
            debugLog(`   LID mappings: ${fileCount.lidMapping} (protected)`);
            debugLog(`   App state: ${fileCount.appState}`);
        }

        // Jalankan cleanup
        const cleanupResult = await cleanupAppStateFiles(sessionId);

        // Cek ukuran setelah cleanup
        const sizeAfter = await getFolderSize(sessionDir);

        if (cleanupResult.cleaned > 0 || sizeBefore !== sizeAfter) {
            debugLog(`✅ Session ${sessionId} cleanup complete:`);
            debugLog(`   Before: ${formatBytes(sizeBefore)} (${fileCount.total} files)`);
            debugLog(`   After: ${formatBytes(sizeAfter)}`);
            debugLog(`   Freed: ${formatBytes(sizeBefore - sizeAfter)}`);
            debugLog(`   Files cleaned: ${cleanupResult.cleaned}`);
        }

        return {
            before: formatBytes(sizeBefore),
            beforeBytes: sizeBefore,
            after: formatBytes(sizeAfter),
            afterBytes: sizeAfter,
            freed: formatBytes(sizeBefore - sizeAfter),
            freedBytes: sizeBefore - sizeAfter,
            cleaned: cleanupResult.cleaned,
            files: cleanupResult.files,
            fileCount,
            warnings: cleanupResult.warnings,
        };
    } catch (error) {
        errorLog(`Error analyzing session ${sessionId}:`, error);
        return {
            before: "Error",
            beforeBytes: 0,
            after: "Error",
            afterBytes: 0,
            freed: "0 Bytes",
            freedBytes: 0,
            cleaned: 0,
            files: [],
            fileCount: { total: 0 },
            warnings: [],
        };
    }
};

/**
 * Cleanup semua session folders (untuk maintenance)
 * @param {string} sessionsDir - Directory sessions
 * @returns {Promise<{totalSessions: number, cleanedSessions: number, totalFreed: string, details: object[]}>}
 */
const cleanupAllSessions = async (sessionsDir = null) => {
    const dir = sessionsDir || getSessionsBaseDir();

    debugLog(`🧹 Running maintenance cleanup for all sessions in: ${dir}`);

    try {
        const sessionDirs = await fs.readdir(dir);

        const results = {
            totalSessions: 0,
            cleanedSessions: 0,
            totalFreedBytes: 0,
            totalBeforeBytes: 0,
            totalAfterBytes: 0,
            largeSessions: [], // Sessions > 10MB
            details: [],
        };

        for (const sessionDir of sessionDirs) {
            const sessionPath = path.join(dir, sessionDir);
            const stat = await fs.stat(sessionPath);

            if (!stat.isDirectory()) {
                continue;
            }

            results.totalSessions++;

            const cleanupResult = await analyzeAndCleanupSession(sessionDir);

            results.totalBeforeBytes += cleanupResult.beforeBytes;
            results.totalAfterBytes += cleanupResult.afterBytes;

            if (cleanupResult.cleaned > 0) {
                results.cleanedSessions++;
            }

            if (cleanupResult.freedBytes > 0) {
                results.totalFreedBytes += cleanupResult.freedBytes;
            }

            // Track large sessions
            if (cleanupResult.beforeBytes > CREDENTIALS_CONFIG.maxSessionFolderSize) {
                results.largeSessions.push({
                    sessionId: sessionDir,
                    size: formatBytes(cleanupResult.beforeBytes),
                    fileCount: cleanupResult.fileCount,
                });
            }

            results.details.push({
                sessionId: sessionDir,
                ...cleanupResult,
            });
        }

        debugLog(`📊 Cleanup summary:`);
        debugLog(`   Total sessions: ${results.totalSessions}`);
        debugLog(`   Sessions cleaned: ${results.cleanedSessions}`);
        debugLog(`   Total storage before: ${formatBytes(results.totalBeforeBytes)}`);
        debugLog(`   Total storage after: ${formatBytes(results.totalAfterBytes)}`);
        debugLog(`   Total space freed: ${formatBytes(results.totalFreedBytes)}`);

        if (results.largeSessions.length > 0) {
            debugLog(`⚠️ Large sessions detected (>10MB):`);
            results.largeSessions.forEach(s => {
                debugLog(`   - ${s.sessionId}: ${s.size} (${s.fileCount?.total || 0} files)`);
                debugLog(`     Session files: ${s.fileCount?.session || 0}, Sender keys: ${s.fileCount?.senderKey || 0}`);
            });
        }

        return results;
    } catch (error) {
        errorLog(`Error during cleanup all sessions:`, error);
        return {
            totalSessions: 0,
            cleanedSessions: 0,
            totalFreedBytes: 0,
            totalBeforeBytes: 0,
            totalAfterBytes: 0,
            largeSessions: [],
            details: [],
        };
    }
};

// ============================================
// EXPORTS
// ============================================
export {
    getSessionDir,
    getSessionsBaseDir,
    backupCredentials,
    restoreFromBackup,
    safeSaveCreds,
    validateAndRecoverCredentials,
    extractPhoneNumberFromCreds,
    healthCheckAllCredentials,
    cleanupCorruptedSessions,
    cleanupAppStateFiles,
    analyzeAndCleanupSession,
    cleanupAllSessions,
    CREDENTIALS_CONFIG,
};
